import React, { useState, useContext, forwardRef } from 'react';
import { Redirect } from 'react-router-dom';
import { EditSaveButton, EditTitle, EditErrorOnSaveAlert, EditDeleteButton, EditCancelButton } from '.';
import Spinner from '../Spinner';
import { Form } from 'react-final-form';
import { EditFormProps } from '.';
import ErrorAlert from '../ErrorAlert';
import 'react-toggle/style.css';
import { Modal, ModalBody } from 'reactstrap';
import CenterSpinner from '../CenterSpinner';
import NavigationPrompt from 'react-router-navigation-prompt';
import Confirm from '../Confirm';
import AppState from 'AppState';
import { hasDeletePermission } from 'services/EntityDefinitionService';
import { createDecorator } from './decorators/focusOnErrorsCustom';
import AppContext from 'components/AppContext';
import EditContext from 'components/shared/edit/EditContext';
import ScopeField from './ScopeField';
import CulturePicker from './CulturePicker';
import arrayMutators from 'final-form-arrays';
import { ValidationErrorSource } from 'util/Misc';
import ValidationErrorAlert, { AlertType } from '~/components/shared/edit/ValidationErrorAlert';
import EditCopyHtmlButton from '~/components/shared/edit/EditCopyHtmlButton';
// #region SMO
import EditPrintViewButton from '~/components/shared/edit/EditPrintViewButton.Smo';
// #endregion SMO

const focusOnErrorsCustom = createDecorator();

const EditForm = forwardRef<HTMLDivElement, EditFormProps>((props, ref) => {
	const [bottomActionsFocused, setBottomActionsFocused] = useState(false);

	const { expirationAlertCountdownStarted } = useContext(AppContext);

	/**
	 * Handles the focus event for adding dyanmic focus CSS class.
	 *
	 * @private
	 * @memberof ListingDynamicResultsTableRow
	 */
	function handleFocusIn() {
		setBottomActionsFocused(true);
	}

	/**
	 * Handles the unfocus event for removing dyanmic focus CSS class.
	 *
	 * @private
	 * @memberof ListingDynamicResultsTableRow
	 */
	function handleBlur() {
		setBottomActionsFocused(false);
	}

	// here we're just extracting variables from the EditFormProps for easier usage
	const {
		initialValues,
		decorators,
		// by default, subscribe to these only.
		subscription = { pristine: true, dirty: true, submitError: true, dirtySinceLastSubmit: true },
		mutators,
		validate,
	} = props;

	const {
		editSessionUniqueKey,
		save,
		redirectPathName,
		redirectSearch,
		redirectCulture,
		entityDefinition,
		isLoading,
		isLoadingInitial,
		isSaving,
		axiosError,
		isCreateMode,
		scopeFieldEnabled,
		validationErrorSource,
	} = useContext(EditContext);

	const {
		user: { authorizedBaseRoutesData },
	} = AppState;

	const formDecorators = [focusOnErrorsCustom];

	if (decorators) {
		formDecorators.push(...decorators);
	}

	const isPermanent = initialValues?.isPermanent === true;
	const isDeletable = initialValues?.isDeletable ?? true;

	return (
		<Form
			key={editSessionUniqueKey}
			onSubmit={save}
			subscription={subscription}
			decorators={formDecorators}
			initialValues={initialValues}
			mutators={{ ...mutators, ...arrayMutators }}
			validate={validate}
			render={(formProps) => {
				// this portion is rendered withing the React Final Form
				// therefore the props we're expecting are coming from there
				// some of those are default, some come based on the subscriptions above
				// https://github.com/final-form/react-final-form#subscription-fieldsubscription
				const { handleSubmit, dirty, submitError, dirtySinceLastSubmit } = formProps;

				const localized = AppState.config.multiSiteEnabled && entityDefinition.localizeMode;

				const showSeparateTitleLine =
					(!entityDefinition.hideKey &&
						!isCreateMode &&
						!entityDefinition.hideEditActions &&
						entityDefinition.versionable) ||
					entityDefinition.scoped ||
					localized;

				if (redirectPathName) {
					console.debug('Redirecting:', redirectPathName + '?' + redirectSearch, redirectCulture);
				}

				if (redirectPathName && entityDefinition.IsEmbeddedInModal) {
					return document.dispatchEvent(new Event('entity-saved'));
				}

				return redirectPathName ? (
					redirectCulture ? (
						<Redirect
							to={{ pathname: redirectPathName, search: redirectSearch, state: { redirectCulture } }}
						/>
					) : (
						<Redirect push to={{ pathname: redirectPathName, search: redirectSearch }} />
					)
				) : !isLoadingInitial ? (
					<>
						<form onSubmit={handleSubmit} className="edit-form" autoComplete="off">
							<NavigationPrompt
								when={dirty && !expirationAlertCountdownStarted && !entityDefinition.hideEditActions}
							>
								{({ isActive, onConfirm, onCancel }) => (
									<Confirm
										onCancel={onCancel}
										isOpen={isActive}
										onConfirm={onConfirm}
										renderTitle={() => 'Warning'}
										renderContents={() =>
											'Are you sure you want to leave this page? Your changes will be lost.'
										}
										confirmButtonText="Leave"
										cancelButtonText="Stay"
									/>
								)}
							</NavigationPrompt>

							<div
								ref={ref}
								id="main-sticky-edit-form-header"
								className="main__sticky-header main__sticky-header--edit-form"
							>
								{showSeparateTitleLine && !entityDefinition.IsEmbeddedInModal ? (
									<div className="edit-form__top-bar">
										<div className="edit-form__top-bar-item--title">
											<EditTitle />
										</div>
										<div className="edit-form__top-bar-item--grow" />
										<div className="edit-form__top-bar-item--grow" />
									</div>
								) : null}

								<div className="edit-form__top-bar">
									<div className="edit-form__top-bar__section">
										{!showSeparateTitleLine && !entityDefinition.IsEmbeddedInModal ? (
											<div className="edit-form__top-bar-item">
												<EditTitle />
											</div>
										) : null}

										{entityDefinition.scoped ? (
											<div className="edit-form__top-bar-item">
												<ScopeField disabled={!scopeFieldEnabled} />
											</div>
										) : null}

										{localized ? (
											<div className="edit-form__top-bar-item">
												<CulturePicker handleSubmit={handleSubmit} />
											</div>
										) : null}

										<div className="edit-form__top-bar-item">
											{!isCreateMode && !entityDefinition.hideEditActions ? (
												<>
													{entityDefinition.hideView
														? null
														: entityDefinition.editViewLiveComponent}
												</>
											) : null}
										</div>
									</div>

									<div className="edit-form__top-bar__section">
										<div className="edit-form__actions-bar">
											{!entityDefinition.hideCancel ? (
												<div className="edit-form__actions-bar-item">
													<EditCancelButton label={entityDefinition.cancelButtonLabel} />
												</div>
											) : null}

											{entityDefinition.copyHtmlMarkup ? (
												<EditCopyHtmlButton entityDefinition={entityDefinition} />
											) : null}

											{/*#region SMO*/}
											{!isCreateMode && !entityDefinition.hidePrintViewAction ? (
												<EditPrintViewButton />
											) : null}
											{/*#endregion SMO*/}

											{!isCreateMode &&
											!entityDefinition.hideDelete &&
											hasDeletePermission(entityDefinition, authorizedBaseRoutesData) ? (
												<EditDeleteButton
													disabled={isSaving || isLoading}
													submitError={!dirtySinceLastSubmit ? submitError : null}
													isPermanent={isPermanent}
													isDeletable={isDeletable}
												/>
											) : null}

											{!entityDefinition.hideEditActions ? (
												<EditSaveButton
													disabled={isSaving || isLoading}
													handleSubmit={handleSubmit}
													submitError={!dirtySinceLastSubmit ? submitError : null}
													isPermanent={isPermanent}
												/>
											) : null}
										</div>
									</div>
								</div>
							</div>
							<div className="main__sticky-after">
								{axiosError ? (
									<ErrorAlert error={axiosError} />
								) : (
									<>
										{
											// show the edit form validation error message if it was a source of errors
											validationErrorSource & ValidationErrorSource.EditForm ? (
												<>
													<EditErrorOnSaveAlert />
													<ValidationErrorAlert
														submitError={submitError}
														alertType={AlertType.Edit}
													/>
												</>
											) : null
										}
									</>
								)}

								{props.render(formProps)}

								<hr className="edit-form__hr" />

								<div
									className={
										bottomActionsFocused
											? 'edit-form__bot-bar'
											: 'edit-form__bot-bar visually-hidden'
									}
									onFocus={handleFocusIn}
									onBlur={handleBlur}
								>
									<div className="edit-form__bot-bar-item--grow" />

									<div className="edit-form__bot-bar-item">
										<div className="edit-form__bot-actions">
											{!isCreateMode &&
											!entityDefinition.hideDelete &&
											hasDeletePermission(entityDefinition, authorizedBaseRoutesData) ? (
												<EditDeleteButton
													disabled={isSaving || isLoading}
													dropdownMenuDirection="up"
													submitError={!dirtySinceLastSubmit ? submitError : null}
													isPermanent={isPermanent}
												/>
											) : null}

											{!entityDefinition.hideEditActions ? (
												<EditSaveButton
													disabled={isSaving || isLoading}
													handleSubmit={handleSubmit}
													dropdownMenuDirection="up"
													submitError={!dirtySinceLastSubmit ? submitError : null}
													isPermanent={isPermanent}
												/>
											) : null}
										</div>
									</div>
								</div>
							</div>
						</form>

						<Modal isOpen={isLoading} centered={true}>
							<ModalBody>
								<Spinner />
							</ModalBody>
						</Modal>
					</>
				) : (
					// if we're still loading the intial entity, display a spinner instead of the edit form contents
					<CenterSpinner />
				);
			}}
		/>
	);
});

export default EditForm;
